import { useEffect, useState } from "react";
import { Loader2, XCircle } from "lucide-react";
import { useNowTicker } from "@/hooks/useNowTicker";
import { Button } from "@/components/ui/base/button";
import { Label } from "@/components/ui/base/label";
import { useStateMachine } from "@/core/arch/state_machine";
import { JobBloc } from "@/application/tools/job_bloc";
import { reconcileJobRepository } from "@/application/tools/job_repository";
import { ToolSheetShell } from "./ToolSheetShell";
import {
  chainedJobId,
  groupCounters,
  scannedTotal,
} from "./reconciler_tallies";
import { entryModeTally, parseBusinessIds } from "./entry_mode_tally";
import { JobStatusBadge } from "./JobStatusBadge";
import { formatDuration } from "./job_display";
import type { ToolSheetProps } from "../tools";

const AUDIT = "entry-mode-audit";
const BACKFILL = "entry-mode-backfill";

/** What each recovered mode means for the merchant, in one line. */
const MODE_NOTES: Record<string, string> = {
  contactless: "A tap. Clears the first-sale step once the lifecycle re-runs.",
  chip: "Inserted, not tapped — does not clear the first-sale step.",
  swipe: "Swiped — does not clear the first-sale step.",
  fallback: "Magstripe fallback — does not clear the first-sale step.",
  manual: "Card-not-present, e.g. an invoice paid online.",
  unresolved:
    "The processor no longer reports a read method. The backfill cannot repair these.",
};

function EntryModeBreakdown({ counters }: { counters: string | null }) {
  const modes = entryModeTally(counters);
  if (modes.length === 0) return null;

  const total = modes.reduce((sum, entry) => sum + entry.count, 0);

  return (
    <div data-entry-mode-breakdown>
      <p className="text-foreground mb-2 text-xs font-medium">
        Recovered entry modes
        <span className="text-muted-foreground ml-2 font-normal">
          {total} {total === 1 ? "charge" : "charges"} resolved
        </span>
      </p>
      <div className="grid grid-cols-[max-content_auto_1fr] items-baseline gap-x-3 gap-y-1">
        {modes.map((entry) => (
          <div key={entry.mode} className="contents">
            <span className="text-foreground text-xs font-medium tabular-nums">
              {entry.count}
            </span>
            <span className="text-foreground text-xs whitespace-nowrap">
              {entry.mode}
            </span>
            <span className="text-muted-foreground text-xs">
              {MODE_NOTES[entry.mode] ?? ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Repairs `Transaction.entryMode` for merchants whose tap was never recorded.
 *
 * Deliberately scoped rather than a whole-table sweep: the affected merchants
 * are already known, one query per merchant is far cheaper than scanning every
 * transaction, and a merchant left out of the box is never read — so the write
 * pass cannot reach them whatever the row filter does.
 *
 * Two passes over the same list. The audit resolves every charge and reports
 * what came back without writing; the backfill repeats it and persists. Run the
 * audit first — `unresolved` is the number that decides whether the backfill is
 * worth running at all.
 */
export function EntryModeBackfillSheet({ onClose }: ToolSheetProps) {
  const [state, bloc] = useStateMachine(
    () =>
      new JobBloc(reconcileJobRepository, "reconcile", {
        followChain: (job) => chainedJobId(job.result),
      }),
  );

  const [raw, setRaw] = useState("");
  const businessIds = parseBusinessIds(raw);

  useEffect(() => {
    bloc.resumeIfRunning();
  }, [bloc]);

  const { phase } = state;
  const job =
    phase.kind === "watching" || phase.kind === "cancelling" ? phase.job : null;

  const isRunning = job?.status === "running" || job?.status === "pending";
  const now = useNowTicker(isRunning);
  const elapsed = job
    ? formatDuration(job.startedAt, job.completedAt, now)
    : null;
  const isTerminal =
    job?.status === "complete" ||
    job?.status === "failed" ||
    job?.status === "cancelled";

  const scanned = scannedTotal(
    groupCounters(job?.counters ?? null),
    job?.processed ?? 0,
  );

  const run = (reconciler: string) =>
    bloc.kickoff({ reconcilers: [reconciler], scope: { businessIds } });

  return (
    <ToolSheetShell
      title="Entry Mode Backfill"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          {phase.kind === "idle" && (
            <>
              <Button
                variant="outline"
                className="flex-1"
                disabled={businessIds.length === 0}
                onClick={() => run(AUDIT)}
              >
                Run audit
              </Button>
              <Button
                className="flex-1"
                disabled={businessIds.length === 0}
                onClick={() => run(BACKFILL)}
              >
                Run backfill
              </Button>
            </>
          )}
          {phase.kind === "kicking_off" && (
            <Button disabled className="w-full">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Starting…
            </Button>
          )}
          {phase.kind === "watching" &&
            (job?.status === "running" || job?.status === "pending") && (
              <Button
                variant="outline"
                onClick={() => bloc.cancel()}
                className="w-full"
              >
                Cancel
              </Button>
            )}
          {isTerminal && (
            <Button
              variant="outline"
              onClick={() => bloc.reset()}
              className="w-full"
            >
              Run again
            </Button>
          )}
        </div>
      }
    >
      <p className="text-muted-foreground text-sm">
        Re-reads settled charges that never recorded an entry mode and recovers
        it from the payment processor. A merchant whose tap went unrecorded
        reads as never having tapped, which holds them on the first-sale setup
        step indefinitely.
      </p>
      <p className="text-muted-foreground mt-3 text-sm">
        Run the audit first. It resolves every charge and reports what came back
        without writing anything — <em>unresolved</em> is the count the
        processor can no longer answer for, and those rows are beyond repair.
      </p>
      <p className="text-muted-foreground mt-3 text-sm">
        Neither pass pays a retroactive activation bonus: the stream consumers
        only fire when a charge first becomes settled, and these are settled
        already. Repairing the entry mode does not by itself advance anyone —
        run <strong>Repair Database → Onboarding lifecycle</strong> afterwards
        for that.
      </p>

      {phase.kind === "idle" && (
        <div className="border-border mt-6 space-y-2 border-t pt-4">
          <Label
            htmlFor="entry-mode-business-ids"
            className="text-foreground text-xs font-medium"
          >
            Merchant IDs
            <span className="text-muted-foreground ml-2 font-normal">
              {businessIds.length === 0
                ? "none yet"
                : `${businessIds.length} ${
                    businessIds.length === 1 ? "merchant" : "merchants"
                  }`}
            </span>
          </Label>
          <textarea
            id="entry-mode-business-ids"
            value={raw}
            onChange={(event) => setRaw(event.target.value)}
            rows={6}
            spellCheck={false}
            placeholder={"d8f93fd8-64d3-4b6b-8757-8f96e49abe24\n…"}
            className="border-border text-foreground w-full rounded-md border p-2 font-mono text-xs"
          />
          <p className="text-subtle-foreground text-xs">
            Business IDs, separated by newlines or commas. Duplicates are
            ignored. Only the merchants listed here are read.
          </p>
        </div>
      )}

      {phase.kind === "error" && (
        <div className="mt-6 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{phase.message}</p>
        </div>
      )}

      {job && (
        <div className="border-border mt-6 space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <JobStatusBadge status={job.status} />
            {elapsed && (
              <span className="text-subtle-foreground text-xs">{elapsed}</span>
            )}
          </div>

          <p className="text-muted-foreground text-xs">
            {scanned === 1 ? "1 charge" : `${scanned} charges`} examined
            {isRunning && " — in progress…"}
          </p>

          <EntryModeBreakdown counters={job.counters} />

          {job.status === "complete" && scanned === 0 && (
            <div className="border-border bg-muted text-muted-foreground rounded-md border p-3 text-sm">
              No settled charges were missing an entry mode for these merchants.
            </div>
          )}

          {job.status === "failed" && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-xs font-medium text-red-700">
                Pass did not complete cleanly
              </p>
              {job.error && (
                <pre className="mt-2 overflow-x-auto text-xs text-red-600">
                  {job.error}
                </pre>
              )}
            </div>
          )}

          <p className="text-subtle-foreground text-xs">Job ID: {job.id}</p>
        </div>
      )}
    </ToolSheetShell>
  );
}
